import {
    Box,
    Chip,
    LinearProgress,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Typography,
    Paper,
    Fade,
    Grow,
    Zoom,
    useTheme,
    alpha,
    keyframes,
    styled,
    Grid,
    Card,
    CardContent,
    CardActionArea
  } from '@mui/material';
  import CheckCircleIcon from '@mui/icons-material/CheckCircle';
  import CancelIcon from '@mui/icons-material/Cancel';
  import StartIcon from '@mui/icons-material/Start';
  import WarningIcon from '@mui/icons-material/Warning';
  import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
  import CelebrationIcon from '@mui/icons-material/Celebration';
  import TrendingUpIcon from '@mui/icons-material/TrendingUp';
  import { useContext, useEffect, useState } from 'react';
  import UserContext from 'Context/UserProvider';
  import { hasChargingRequirement, hasSharedInfrastructure } from 'utils/categoryUtils';
  import DashboardCard from '../common/DashboardCard';

  // Animated progress ring
  const ProgressRing = styled(Box)(({ theme }) => ({
    position: 'relative',
    width: 120,
    height: 120,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&::before': {
      content: '""',
      position: 'absolute',
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      background: `conic-gradient(from 0deg, ${theme.palette.primary.main} 0deg, ${theme.palette.primary.main} var(--progress), ${alpha(theme.palette.primary.main, 0.1)} var(--progress), ${alpha(theme.palette.primary.main, 0.1)} 360deg)`,
      animation: 'progressSpin 2s ease-out',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      width: '80%',
      height: '80%',
      borderRadius: '50%',
      background: theme.palette.background.paper,
      boxShadow: 'inset 0 0 10px rgba(0,0,0,0.1)',
    }
  }));

  // Floating animation
  const float = keyframes`
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
  `;

  // Pulse animation
  const pulse = keyframes`
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
  `;

  // Progress spin animation
  const progressSpin = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  `;

  // Success celebration animation
  const celebrate = keyframes`
    0% { transform: scale(0) rotate(0deg); opacity: 0; }
    50% { transform: scale(1.2) rotate(180deg); opacity: 1; }
    100% { transform: scale(1) rotate(360deg); opacity: 1; }
  `;

  // Card hover animation
  const cardHover = keyframes`
    0% { transform: translateY(0px) scale(1); }
    100% { transform: translateY(-8px) scale(1.02); }
  `;

  const AnimatedCard = styled(Card)<{ completed?: boolean; index?: number }>(({ theme, completed, index }) => ({
    height: '100%',
    borderRadius: 16,
    background: completed 
      ? `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)} 0%, ${alpha(theme.palette.success.light, 0.05)} 100%)`
      : `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)} 0%, ${alpha(theme.palette.grey[100], 0.7)} 100%)`,
    border: `2px solid ${completed ? theme.palette.success.main : theme.palette.grey[200]}`,
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    animation: `fadeInUp 0.6s ease-out ${(index || 0) * 0.1}s both`,
    position: 'relative',
    overflow: 'visible',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      background: completed 
        ? `linear-gradient(90deg, ${theme.palette.success.main}, ${theme.palette.success.light})`
        : theme.palette.grey[300],
      borderRadius: '16px 16px 0 0',
    },
    '&:hover': {
      animation: `${cardHover} 0.3s ease-out forwards`,
      boxShadow: completed 
        ? `0 12px 40px ${alpha(theme.palette.success.main, 0.25)}`
        : `0 12px 40px ${alpha(theme.palette.grey[400], 0.15)}`,
      borderColor: completed ? theme.palette.success.dark : theme.palette.grey[400],
    },
    '& .MuiCardActionArea-root': {
      height: '100%',
      padding: '20px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      textAlign: 'left',
    }
  }));

  const fadeInUp = keyframes`
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  `;

  function GettingStarted({ onboardingProgress }: { onboardingProgress: any }) {
    const { userInfo } = useContext(UserContext);
    const theme = useTheme();
    const [animateProgress, setAnimateProgress] = useState(false);
    const [showCelebration, setShowCelebration] = useState(false);

    const steps = [
      {
        label: 'Join Charging Network',
        href: '/share/business/partner-charging-network',
        complete: onboardingProgress?.partnerChargingNetworkSubscription,
        icon: '🌐',
        description: 'Connect to the shared charging infrastructure'
      },
      {
        label: 'Add Charge Points',
        href: '/share/business/charging-infrastructure/dwellings',
        complete: onboardingProgress.chargePointCount > 0,
        icon: '🔌',
        description: 'Set up your charging infrastructure'
      },
      {
        label: 'Add Electric Vehicles',
        href: '/share/business/electric-vehicles',
        complete: onboardingProgress.electricVehicleCount > 0,
        icon: '🚗',
        description: 'Register your electric fleet'
      },
      {
        label: 'Add Drivers',
        href: '/common/user/add',
        complete: onboardingProgress.driverCount > 0,
        icon: '👥',
        description: 'Invite your team members'
      },
      {
        label: 'Add Agreement Documents',
        href: '/share/business/agreement-documents',
        complete: onboardingProgress.agreementDocumentSetCount > 0,
        icon: '📄',
        description: 'Upload your legal documents'
      },
      {
        label: 'Add Site Access Policies',
        href: '/share/business/site-access-policies',
        complete: onboardingProgress.siteAccessPolicyCount > 0,
        icon: '🔒',
        description: 'Configure access controls'
      },
      {
        label: 'Add Rate Cards',
        href: '/share/business/charging-partners/rate-cards',
        complete: onboardingProgress.rateCardCount > 0,
        icon: '💰',
        description: 'Set your pricing structure'
      },
    ];

    if (hasSharedInfrastructure(userInfo.businessCategory)) {
      steps.push({
        label: 'Create Charging Provider Partnerships',
        href: '/share/business/charging-partners',
        complete: onboardingProgress.chargeProviderCount > 0,
        icon: '🤝',
        description: 'Partner with charging providers'
      });
    }

    if (hasChargingRequirement(userInfo.businessCategory)) {
      steps.push({
        label: 'Create Charging Consumer Partnerships',
        href: '/share/business/charging-partners',
        complete:
          onboardingProgress.chargeConsumerCount > 0 &&
          onboardingProgress.chargeConsumerAcceptCount > 0
            ? 'done'
            : onboardingProgress.chargeConsumerCount > 0
              ? 'partial'
              : false,
        icon: '⚡',
        description: 'Connect with charging consumers'
      });
    }

    const totalSteps = steps.length;
    const completedSteps = steps.filter((step) => step.complete === true || step.complete === 'done').length;
    const progress = Math.round((completedSteps / totalSteps) * 100);

    useEffect(() => {
      setAnimateProgress(true);
      if (progress === 100) {
        setTimeout(() => setShowCelebration(true), 1000);
      }
    }, [progress]);

    const getProgressColor = () => {
      if (progress >= 80) return theme.palette.success.main;
      if (progress >= 60) return theme.palette.warning.main;
      if (progress >= 40) return theme.palette.info.main;
      return theme.palette.error.main;
    };

    const getProgressMessage = () => {
      if (progress === 100) return "🎉 You're all set! Welcome to the future of charging!";
      if (progress >= 80) return "🚀 Almost there! You're doing amazing!";
      if (progress >= 60) return "💪 Great progress! Keep up the momentum!";
      if (progress >= 40) return "🌟 You're on the right track!";
      if (progress >= 20) return "✨ Getting started! Every step counts!";
      return "🎯 Ready to begin your journey? Let's get started!";
    };

    const renderStep = (step: any, index: number) => {
      const status = step.complete === true || step.complete === 'done'
        ? 'success'
        : step.complete === 'partial'
          ? 'warning'
          : 'error';

      const icon =
        status === 'success' ? (
          <Box sx={{ 
            animation: `${pulse} 2s infinite`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 60,
            height: 60,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.light})`,
            color: 'white',
            fontSize: '1.8rem',
            mb: 2,
            boxShadow: `0 4px 20px ${alpha(theme.palette.success.main, 0.3)}`
          }}>
            {step.icon}
          </Box>
        ) : status === 'warning' ? (
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 60,
            height: 60,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.light})`,
            color: 'white',
            fontSize: '1.8rem',
            mb: 2,
            boxShadow: `0 4px 20px ${alpha(theme.palette.warning.main, 0.3)}`
          }}>
            {step.icon}
          </Box>
        ) : (
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 60,
            height: 60,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${theme.palette.grey[400]}, ${theme.palette.grey[300]})`,
            color: 'white',
            fontSize: '1.8rem',
            mb: 2,
            boxShadow: `0 4px 20px ${alpha(theme.palette.grey[400], 0.3)}`
          }}>
            {step.icon}
          </Box>
        );

      const chipLabel =
        status === 'success' ? '✅ Complete' : status === 'warning' ? '⏳ In Progress' : '⏸️ Pending';

      return (
        <Grid item xs={12} sm={6} md={4} key={index}>
          <AnimatedCard
            completed={status === 'success'}
            index={index}
          >
            <CardActionArea
              component="a"
              href={step.href}
              sx={{
                textDecoration: 'none',
                color: 'inherit',
                height: '100%',
                '&:hover': {
                  textDecoration: 'none',
                  color: 'inherit',
                }
              }}
            >
              <CardContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  {icon}
                </Box>
                
                <Typography 
                  variant="h6" 
                  fontWeight={700} 
                  color={status === 'success' ? 'success.main' : 'text.primary'}
                  sx={{ mb: 1, textAlign: 'center' }}
                >
                  {step.label}
                </Typography>
                
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ mb: 2, textAlign: 'center', flex: 1 }}
                >
                  {step.description}
                </Typography>
                
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Chip 
                    label={chipLabel} 
                    color={status as any} 
                    size="small"
                    sx={{ 
                      fontWeight: 600,
                      animation: status === 'success' ? `${float} 3s ease-in-out infinite` : 'none',
                      boxShadow: status === 'success' ? `0 2px 8px ${alpha(theme.palette.success.main, 0.3)}` : 'none'
                    }}
                  />
                </Box>
              </CardContent>
            </CardActionArea>
          </AnimatedCard>
        </Grid>
      );
    };

    return (
      <DashboardCard title="Your Journey to Success" icon={<RocketLaunchIcon />} noPadding>
        <Box sx={{ p: 3 }}>
          {/* Progress Header */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            mb: 3,
            p: 2,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
          }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" fontWeight={700} color="primary.main" gutterBottom>
                {progress}%
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                {getProgressMessage()}
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={progress} 
                sx={{ 
                  height: 8, 
                  borderRadius: 4,
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  '& .MuiLinearProgress-bar': {
                    background: `linear-gradient(90deg, ${getProgressColor()}, ${alpha(getProgressColor(), 0.7)})`,
                    borderRadius: 4,
                    transition: 'width 1.5s cubic-bezier(0.4, 0, 0.2, 1)',
                  }
                }} 
              />
            </Box>
            
            {/* Animated Progress Ring */}
            <Box sx={{ position: 'relative', ml: 2 }}>
              <ProgressRing 
                sx={{ 
                  '--progress': `${progress * 3.6}deg`,
                  animation: animateProgress ? `${progressSpin} 2s ease-out` : 'none'
                }}
              />
              <Typography 
                variant="h6" 
                sx={{ 
                  position: 'absolute', 
                  top: '50%', 
                  left: '50%', 
                  transform: 'translate(-50%, -50%)',
                  fontWeight: 700,
                  color: 'primary.main'
                }}
              >
                {progress}%
              </Typography>
            </Box>
          </Box>

          {/* Celebration Animation */}
          {showCelebration && (
            <Fade in={showCelebration}>
              <Box sx={{ 
                textAlign: 'center', 
                mb: 3, 
                p: 2,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.light, 0.05)} 100%)`,
                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
              }}>
                <CelebrationIcon sx={{ fontSize: 40, color: 'success.main', mb: 1, animation: `${celebrate} 1s ease-out` }} />
                <Typography variant="h6" color="success.main" fontWeight={600}>
                  🎉 Congratulations! 🎉
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  You've completed your onboarding! Welcome to the future of electric vehicle charging.
                </Typography>
              </Box>
            </Fade>
          )}

          {/* Steps Grid */}
          <Box>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrendingUpIcon color="primary" />
              Your Progress Steps
            </Typography>
            <Grid container spacing={2}>
              {steps.map(renderStep)}
            </Grid>
          </Box>

          {/* Completion Stats */}
          {completedSteps > 0 && (
            <Box sx={{ 
              mt: 3, 
              p: 2, 
              borderRadius: 2, 
              background: alpha(theme.palette.success.main, 0.05),
              border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`
            }}>
              <Typography variant="body2" color="success.main" fontWeight={600}>
                ✅ {completedSteps} of {totalSteps} steps completed
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {totalSteps - completedSteps} steps remaining to complete your setup
              </Typography>
            </Box>
          )}
        </Box>
      </DashboardCard>
    );
  }

  export default GettingStarted;
  